import { useEffect, useMemo, useRef } from 'react';
import type { KeyboardEvent } from 'react';

import { Alignment, Fit, Layout } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { useRiveMachine } from '../../../../hooks/rive/useRiveMachine';
import { useStores } from '../../../../stores/useStores';

import { guardRepeatClicks } from './bannerCtaAction';
import styles from './RiveHeader.module.scss';
import { bindTocLobbyBanner, type TocLobbyBannerValues } from './tocLobbyBanner';
import { useBannerCta } from './useBannerCta';

interface TocLobbyHeaderProps {
  riveUrl: string;
  values: TocLobbyBannerValues | null;
  bannerClassName?: string;
}

/** The ToC lobby file in place of the lobby banner; a tap runs the admin banner's CTA. Key it by `riveUrl`. */
const TocLobbyHeader: React.FC<TocLobbyHeaderProps> = observer(({ riveUrl, values, bannerClassName }) => {
  const { uiStore } = useStores();
  const layout = useMemo(() => new Layout({ fit: Fit.Cover, alignment: Alignment.Center }), []);
  const { rive, RiveComponent } = useRiveMachine({
    src: riveUrl,
    autoBind: true,
    isTouchScrollEnabled: true,
    layout,
    onLoadError: () => uiStore.markTocLobbyRiveFailed(riveUrl),
  });

  useEffect(() => {
    const vm = rive?.viewModelInstance;
    if (!vm || !values) return;
    bindTocLobbyBanner(vm, values);
  }, [rive, values]);

  // Like any lobby banner: the admin's CTA, read at tap time; with none, the banner is not tappable.
  const ctaAction = useBannerCta();
  const ctaActionRef = useRef(ctaAction);
  ctaActionRef.current = ctaAction;
  const onCta = useMemo(() => guardRepeatClicks(() => ctaActionRef.current?.()), []);
  const isCtaOn = ctaAction !== null;
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onCta();
  };

  return (
    <div
      className={classNames(styles.tocLobby, { [styles.tocLobbyInteractive]: isCtaOn }, bannerClassName)}
      data-testid="toc-lobby-banner"
      role={isCtaOn ? 'button' : undefined}
      tabIndex={isCtaOn ? 0 : undefined}
      onClick={isCtaOn ? onCta : undefined}
      onKeyDown={isCtaOn ? onKeyDown : undefined}
    >
      <RiveComponent className={styles.tocLobbyRive} />
    </div>
  );
});

export default TocLobbyHeader;
